import { BigNumberish } from '@ethersproject/bignumber'
import { useMemo } from 'react'
import useSWR, { Fetcher } from 'swr'
import { useAccount, useBalance, useProvider } from 'wagmi'
import { DEFAULT_CHAIN_ID, getAddressesForGobblerInstance } from '../../config'
import { getRpcProviderForChain, CHAIN_IDS } from '../../web3/chains'
import { artGobblersOnChainApi } from '../../web3/onchain-api/gobblers'

import { formatUnits } from '@ethersproject/units'
import { useSmartContractReadCall } from 'hooks/react-query-typechain/useSmartContractReadCall'

import { createDecimal, formatErc20Default } from '../../utils/format-number'
import useContracts from 'hooks/mint/useContracts'
interface GooSnapshot {
  emissionMultiple: BigNumberish
  lastBalance: BigNumberish
  lastTimestamp: BigNumberish
}

interface GooSnapshotWithTimestamp extends GooSnapshot {
  retrievedTimestamp: number
}

const loadGooBalanceSnapshot = async (
  chainId: number,
  userAddress: string,
): Promise<GooSnapshotWithTimestamp | undefined> => {
  if (!chainId) {
    return undefined
  }
  const provider = getRpcProviderForChain(chainId)
  const snapshot = await artGobblersOnChainApi.getEmissionsForUser(
    userAddress,
    getAddressesForGobblerInstance(chainId).ART_GOBBLERS,
    provider,
  )
  const res: GooSnapshotWithTimestamp = {
    retrievedTimestamp: Date.now(),
    ...snapshot,
  }
  return res
}

const useGooBalanceSnapshot = (
  chainId: number | undefined,
  userAddress: string | undefined,

  swrConfigOverrides?: Fetcher<GooSnapshotWithTimestamp> | undefined,
) => {
  const hasArgs = chainId && userAddress

  const gooBalanceData = useSWR(
    hasArgs ? [chainId, userAddress, 'goo-snapshot'] : undefined,
    loadGooBalanceSnapshot,
    {
      ...swrConfigOverrides,
    },
  )

  return gooBalanceData
}

const useGooBalance = (walletAddress: string | undefined | null) => {
  const [_gobblerContract, gooContract] = useContracts(DEFAULT_CHAIN_ID)

  const gooBalanceResult = useSmartContractReadCall(gooContract, 'balanceOf', {
    callArgs: [walletAddress as string],
    enabled: !!walletAddress,
  })

  return gooBalanceResult
}

const useFormattedGooAndGobblerInfo = () => {
  const accountData = useAccount()
  const address = accountData?.address

  const chainId = DEFAULT_CHAIN_ID
  const [gobblerContract, gooContract] = useContracts(chainId)

  // Goo Balance
  const gooBalanceResult = useSmartContractReadCall(gooContract, 'balanceOf', {
    callArgs: [address as string], // safe to cast because `enabled` is set
    enabled: !!address,
  })
  const gooBalance = gooBalanceResult.data ? formatUnits(gooBalanceResult.data, 18) : undefined

  // Gobbler Mint Price
  const gobblerMintPriceInGooResult = useSmartContractReadCall(gobblerContract, 'gobblerPrice')
  const gobblerPrice = formatUnits(gobblerMintPriceInGooResult.data ?? 0, 18)

  // FMT #1
  let gooBalanceFmt = undefined
  if (gooBalance !== undefined) {
    const gooBalanceDecimal = createDecimal(gooBalance.toString())
    gooBalanceFmt = formatErc20Default(gooBalanceDecimal)
  }

  // FMT #2
  let gobblerPriceInGoopFmt = undefined
  if (gobblerPrice !== undefined) {
    const gobblerPriceDecimal = createDecimal(gobblerPrice.toString())
    gobblerPriceInGoopFmt = formatErc20Default(gobblerPriceDecimal) // ?.div(GOO_DECIMALS.toString()))
  }

  return [gooBalanceFmt, gobblerPriceInGoopFmt]
}

export { useGooBalanceSnapshot, useGooBalance, useFormattedGooAndGobblerInfo }
